import { Ref, ref } from "vue";
import * as yup from "yup";
import { addressFormSchema } from "@/composables/forms/useAddressForm";
import { AddressFormInputs } from "@/models/application";
import { $api } from "@/services/api";
import { Stipulation } from "@/models/opal";

const getIndexFromPath = (path: string) => {
  const matches = path.match(/\[(\d+)\]/);
  return matches ? parseInt(matches[1], 10) : null;
};

// Function to group Yup ValidationErrors by array index
const groupErrorsByArrayIndex = (error: any) => {
  const errorsByIndex = {} as Record<string, any>;
  const indexKeys = [] as string[];
  error.inner.forEach((innerError: yup.ValidationError) => {
    // Extract the array path and index, e.g., 'forms[0].name' -> 'forms[0]'
    const match = innerError.path ? innerError.path.match(/(.+?\[\d+\])/) : false;

    // Extract the index e.g. 'forms[0]' -> 0
    const index = innerError.path ? getIndexFromPath(innerError.path || "") : -1;

    if (match) {
      let arrayPathWithIndex = match[0];

      arrayPathWithIndex =
        index !== null && Number(index) >= 0 ? `Address ${index + 1}` : arrayPathWithIndex;

      if (!errorsByIndex[arrayPathWithIndex]) {
        errorsByIndex[arrayPathWithIndex] = [];
      }

      if (!indexKeys.includes(arrayPathWithIndex)) {
        indexKeys.push(arrayPathWithIndex);
      }
      // Add the specific error message to the corresponding index group
      errorsByIndex[arrayPathWithIndex].push(innerError.message);
    }
  });

  return { errorsByIndex, indexKeys };
};

export default function useAddressForms(data: {
  stipulation: Ref<Stipulation>;
  forms: Ref<Array<AddressFormInputs>>;
  savingChanges: Ref<boolean>;
}) {
  const errors = ref<string[]>([]);

  const formsArraySchema = yup.object().shape({
    forms: yup
      .array()
      .of(addressFormSchema)
      .min(1, "At least one form is required") // Ensures the array is not empty
      .required("Forms field is required"),
  });

  async function validateAddressForms() {
    try {
      const validatedData = await formsArraySchema.validate(
        { forms: data.forms.value },
        { abortEarly: false }
      );
      console.log("All forms are valid!", validatedData);
      return { errors: [], valid: true, errorsByIndex: undefined, indexKeys: [] };
    } catch (error: any) {
      if (!(error instanceof yup.ValidationError) || !error.inner) {
        data.savingChanges.value = false;
        return null; // Not a ValidationError or no inner errors to process
      }

      const errorsByIndex = groupErrorsByArrayIndex(error) || {};

      return {
        errors: { ...error },
        errorsByIndex,
        indexKeys: errorsByIndex.indexKeys,
        valid: false,
      };
    }
  }

  async function updateAddressMismatchStipAsync() {
    try {
      const response = await $api.applicant.updateAddressMismatchStip(
        data.stipulation.value?.applicantId,
        data.stipulation.value?.id,
        {
          applicationId: data.stipulation.value?.applicationId,
          applicantId: data.stipulation.value?.applicantId,
          stipulationId: data.stipulation.value?.id,
          addresses: data.forms.value,
        }
      );

      return response;
    } catch (error) {
      console.log("Error saving addresses");
      return false;
    }
  }

  return {
    errors,
    formsArraySchema,
    validateAddressForms,
    updateAddressMismatchStipAsync,
  };
}
